import { getCountryName } from '@monoimin/utils/formatters';

import { getLocalePrefix, realEstateFiltersDict } from '@monoimin/intl';

import {
  ESTATE_FILTER_KEYS,
  ESTATE_PAGE_ROLES,
  PUBLIC_ESTATE_FILTERS,
  STRAPI_FILTER_OPERATORS,
  FILTER_VARIANTS,
} from '../../constants';

import { OBJECT_TYPE_PAGE_SLUG_CONFIGS } from '../../configs/page-slug-configs';
import { FILTER_CONFIGS_UTILS } from '../../configs/filter-configs-utils';

import { createRangeFromFiltersStateValue } from './createRangeFromFiltersStateValue';

import type {
  Locales,
  StrapiCountry,
  StrapiCity,
  StrapiDeveloper,
  StrapiFilterParams,
  EnOrRuLocale,
} from '@monoimin/sb-types';
import type {
  SelectItem,
  RangeFormatter,
  FilterConfigs,
  ExtendedFilterConfigs,
  EstateFillterKeys,
  PropertyTypeResponse,
  StrapiFilterParamsWithOperators,
  OptionFilters,
  ExtendedOptionFilters,
  ExtendedOptionFilterConfigs,
  ExtendedOptionFilterConfig,
  OptionFilterConfig,
  FilterUtils,
  FilterStateValue,
  FiltersState,
} from '../../types';

type StateWithOnlyCountry = FiltersState & {
  country: Array<string>;
};

export const checkIsEmptyState = (state?: FiltersState, exeptKey?: string) => {
  if (!state) {
    return true;
  }

  return Object.entries(state).every(([key, value]) => {
    if (key === exeptKey) {
      return true;
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return !value;
  });
};

export const checkIsOneValueInState = <T extends FiltersState>(
  state: FiltersState,
  field: string
): state is T => {
  if (typeof field !== 'string' || !state[field]) {
    return false;
  }

  const fieldValue = state[field];

  const isOneValueInState =
    Array.isArray(fieldValue) && fieldValue.length === 1;

  const isOnlyValueInState = checkIsEmptyState(state, field);

  return isOneValueInState && isOnlyValueInState;
};

export const defineMainPageSubmitParams = (state: FiltersState) => {
  if (checkIsOneValueInState<StateWithOnlyCountry>(state, 'country')) {
    return {
      ...state,
      country: [],
    };
  }

  return state;
};

export const defineMainPageSubmitPathname = (
  state: FiltersState,
  locale: Locales
) => {
  const localePrefix = getLocalePrefix(locale);

  if (
    checkIsOneValueInState<StateWithOnlyCountry>(
      state,
      ESTATE_FILTER_KEYS.COUNTRY
    )
  ) {
    const contrySlug = state.country[0];

    return `${localePrefix}/real-estate/${contrySlug}`;
  }

  return `${localePrefix}/real-estate/search`;
};

export const percentFormatter: RangeFormatter = (value) => {
  if (Array.isArray(value)) {
    return `${value[0]} % - ${value[1]} %`;
  }

  return '';
};

/**
 * Выбрали города в фильтре - получили список слагов стран, в которых находятся эти города.
 * Используем в компоненте CityFilter, фильтре по городу.
 *
 * @param countries - Массив стран.
 * @param cities - Массив городов.
 * @param value - Выбранные в фильтре города (объект, массив объектов).
 * @returns Возвращает список слагов стран, города которых указаны в value
 */
export const collectCountriesSlugs = (
  countries: StrapiCountry[],
  cities: StrapiCity[],
  value: SelectItem | Array<SelectItem>
) => {
  if (!value) {
    return [];
  }

  const valueForProcessing = Array.isArray(value) ? value : [value];

  return valueForProcessing.reduce<Array<string>>((acc, item) => {
    const city = cities.find((city) => city.slug === item.value);

    if (!city) {
      return acc;
    }

    const countrySlug =
      city.country && 'slug' in city.country ? city.country.slug : '';

    const country = countries.find((country) => country.slug === countrySlug);

    if (!country?.slug) {
      return acc;
    }

    return [...acc, country.slug];
  }, []);
};

export const createCountriesForFilter = (
  countries: StrapiCountry[],
  locale: Locales
): SelectItem[] => {
  if (countries) {
    return countries.map((item) => {
      return {
        id: item.id,
        title: getCountryName(item.code, locale),
        value: item.slug,
      };
    });
  }

  return [];
};

export const createCitiesForFilter = (cities: StrapiCity[]): SelectItem[] => {
  if (cities) {
    return cities.map((item) => {
      return {
        id: item.id,
        title: item.title,
        value: item.slug,
      };
    });
  }

  return [];
};

export const createDevelopersForFilter = (
  developers: StrapiDeveloper[]
): SelectItem[] => {
  if (developers) {
    return developers.map((item) => {
      return {
        id: item.id,
        title: item.title,
        value: item.title.toLowerCase(),
      };
    });
  }

  return [];
};

export const createOptionsForFilter = (
  options: Array<string>
): SelectItem[] => {
  return options.map((item, index) => {
    return {
      id: index,
      title: item,
      value: item,
    };
  });
};

export const createObjectTypesForFilter = (
  objectTypes: Array<string>,
  locale: EnOrRuLocale
): SelectItem[] => {
  const dict = realEstateFiltersDict.objectTypes[locale];

  return objectTypes.map((item, index) => {
    const config = dict.find((dictItem) => dictItem.value === item);

    return {
      id: index,
      title: config?.title,
      value: item,
    };
  });
};

export const updateFiltersState = ({
  key,
  selectedData,
  filtersState,
}: {
  key: EstateFillterKeys;
  selectedData: SelectItem | SelectItem[];
  filtersState?: FiltersState;
}) => {
  if (Array.isArray(selectedData)) {
    return {
      ...filtersState,
      [key]: selectedData.reduce<string[]>((acc, item) => {
        if (item.value) {
          return [...acc, item.value];
        }

        return acc;
      }, []),
    };
  }

  if (selectedData?.value) {
    return {
      ...filtersState,
      [key]: [selectedData.value],
    };
  }

  if (filtersState) {
    return filtersState;
  }

  return {};
};

export const defineFiltersForCountryPage = (
  country: StrapiCountry,
  propertyType?: PropertyTypeResponse
) => {
  if (!country && !propertyType) {
    return undefined;
  }

  if (propertyType && country.slug) {
    return {
      ...propertyType.filtersState,
      object_type: [...propertyType.filtersState.object_type],
      country: [country.slug],
    };
  }

  if (country.slug) {
    return {
      country: [country.slug],
    };
  }
};

export const collectParamsFilterValues = <T extends SelectItem>(
  data: T[],
  params: FilterStateValue
) => {
  if (Array.isArray(params)) {
    return data.filter((item) => {
      const passItem = params.find((value) => value === item.value);

      return passItem;
    });
  }

  return [];
};

export const defineSelectedValueTitle = <T extends SelectItem>(
  value: T | T[] | undefined,
  placeholder: string
) => {
  if (Array.isArray(value) && value.length) {
    return value.map((item) => item.title).join(', ');
  }

  if (value && 'title' in value) {
    return value.title;
  }

  return placeholder;
};

/**
 * Пределяем, есть ли в массиве параметров нужный нам slug.
 *
 * @param params - Параметры ключа стейта FiltersState. Параметры - значения из квери.
 * @param compareItem - Данные с ключом slug для мэтча c параметрами. Пример: объекты стран/городов.
 */
export const findParamsSlugMatch = <
  T extends { slug?: string | undefined | unknown }
>(
  params: FilterStateValue,
  compareItem: Array<T> | T | undefined
) => {
  if (!Array.isArray(params) || !compareItem) {
    return false;
  }

  if (Array.isArray(compareItem)) {
    const slugs = compareItem.map((item) => String(item?.slug));

    return Boolean(
      params.find((param) => {
        return slugs.find((slug) => slug === param);
      })
    );
  }

  return Boolean(
    params.find((param) => {
      return param === compareItem?.slug;
    })
  );
};

export const filterCitiesByCountryParam = (
  cities: Array<StrapiCity>,
  countryFilterValue: FilterStateValue
) => {
  if (!Array.isArray(countryFilterValue) || !countryFilterValue?.length) {
    return cities;
  }

  return cities.filter((item) => {
    const { country } = item;

    if (country && 'slug' in country) {
      return findParamsSlugMatch(countryFilterValue, country);
    }

    return false;
  });
};

export const filterCountriesByEstatesCount = (items: Array<StrapiCountry>) => {
  return items.filter((item) => {
    const estates = item.estates as { count: number };

    return estates?.count > 0;
  });
};

// Добавляем кавычки для корректной фильтрации на бекенде
export const surroundWithQuotes = (
  data: string | Array<string> | undefined
): string | Array<string> => {
  if (!data) {
    return '';
  }
  if (typeof data === 'string' && data.includes(`"`)) {
    return data;
  }

  if (typeof data === 'string') {
    return `"${data}"`;
  }

  return data.map(surroundWithQuotes) as Array<string>;
};

export const prepareEstateFilterParams = <
  T extends StrapiFilterParams | undefined
>(
  filtersParams: T
) => {
  if (!filtersParams) {
    return PUBLIC_ESTATE_FILTERS;
  }

  const mergedFilterParams = { ...PUBLIC_ESTATE_FILTERS, ...filtersParams };
  const result: StrapiFilterParams = {};

  Object.entries(mergedFilterParams).forEach(([key, value]) => {
    const isMultiselectField =
      key === ESTATE_FILTER_KEYS.OBJECT_TYPE ||
      key === ESTATE_FILTER_KEYS.OBJECT_STAGE;

    if (!isMultiselectField) {
      result[key] = value;
      return;
    }

    const isValidValue =
      typeof value === 'object' && STRAPI_FILTER_OPERATORS.CONTAINSI in value;

    if (!isValidValue) {
      result[key] = value;
      return;
    }

    const operatorValue = value[STRAPI_FILTER_OPERATORS.CONTAINSI];

    const isValidOperatorValue =
      Array.isArray(operatorValue) || typeof operatorValue === 'string';

    if (isValidOperatorValue) {
      result[key] = {
        [STRAPI_FILTER_OPERATORS.CONTAINSI]: surroundWithQuotes(operatorValue),
      };
    } else {
      result[key] = value;
    }
  });

  return result;
};

export const createFilterParamsForStaticPage = (
  slug: string | string[] | undefined,
  pageRole: ESTATE_PAGE_ROLES | undefined
): FiltersState => {
  if (!Array.isArray(slug) || !pageRole) {
    return {};
  }

  const [firstSegment, secondSegment] = slug;

  if (pageRole === ESTATE_PAGE_ROLES.COUNTRY) {
    return {
      [ESTATE_FILTER_KEYS.COUNTRY]: firstSegment,
    };
  }

  if (pageRole === ESTATE_PAGE_ROLES.OBJECT_STAGE) {
    return {
      [ESTATE_FILTER_KEYS.OBJECT_STAGE]: firstSegment,
    };
  }

  const isPropertyTypeRole = pageRole === ESTATE_PAGE_ROLES.PROPERTY_TYPE;

  const segmentToCompare = isPropertyTypeRole ? firstSegment : secondSegment;

  const objectType = Object.entries(OBJECT_TYPE_PAGE_SLUG_CONFIGS).find(
    ([_, item]) => {
      return segmentToCompare === item.slug;
    }
  );

  const objectTypeValue = objectType?.[0] || null;

  if (isPropertyTypeRole) {
    return {
      [ESTATE_FILTER_KEYS.OBJECT_TYPE]: objectTypeValue,
    };
  }

  if (pageRole === ESTATE_PAGE_ROLES.COUNTRY_PROPERTY_TYPE) {
    return {
      [ESTATE_FILTER_KEYS.COUNTRY]: firstSegment,
      [ESTATE_FILTER_KEYS.OBJECT_TYPE]: objectTypeValue,
    };
  }
  return {};
};

export const separateFilterConfigs = <T extends { hideUnderSpoiler?: boolean }>(
  configs: Array<T>
) => {
  const result: { hiddenConfigs: Array<T>; visibleConfigs: Array<T> } = {
    hiddenConfigs: [],
    visibleConfigs: [],
  };

  configs.forEach((item) => {
    const key = item.hideUnderSpoiler ? 'hiddenConfigs' : 'visibleConfigs';
    result[key].push(item);
  });

  return result;
};

const mergeParams = (
  operator:
    | typeof STRAPI_FILTER_OPERATORS.AND
    | typeof STRAPI_FILTER_OPERATORS.OR,
  currentParams: StrapiFilterParamsWithOperators,
  newParams: StrapiFilterParamsWithOperators | undefined
) => {
  const currentValue = currentParams[operator] || [];

  const newValue =
    newParams && Array.isArray(newParams[operator]) ? newParams[operator] : [];

  return {
    [operator]: newValue ? [...currentValue, ...newValue] : currentValue,
  };
};

/**
 *
 * @param config - Главный фильтр конфиг или конфиг дочернего option.
 * @param state - Текущий стейт с фильтрами.
 * @returns Возвращает объект с фильтрами, используя функцию filterParamsCreator.
 */
const createFilterParamsForConfig = (
  config: ExtendedFilterConfigs[number] | ExtendedOptionFilterConfig,
  state: FiltersState | undefined
) => {
  const { filterParamsCreator, filterVariant } = config;

  if (typeof filterParamsCreator !== 'function' || !state) {
    return undefined;
  }

  const value =
    filterVariant === FILTER_VARIANTS.RANGE
      ? createRangeFromFiltersStateValue(
          state[config.filterKey.from],
          state[config.filterKey.to]
        )
      : state[config.filterKey];

  return filterParamsCreator(value);
};

export const createFilterParamsFromConfigs = (
  configs: ExtendedFilterConfigs | ExtendedOptionFilterConfigs,
  state: FiltersState | undefined
): StrapiFilterParams | undefined => {
  if (!state) {
    return undefined;
  }

  return [...configs].reduce<StrapiFilterParamsWithOperators>((acc, item) => {
    const { options = [] } = item;

    const filterParams = createFilterParamsForConfig(item, state);

    const optionsFilterParams = options.reduce((acc, option) => {
      return Object.assign(
        acc,
        createFilterParamsFromConfigs(option.filterConfigs, state)
      );
    }, {});

    return Object.assign(
      acc,
      filterParams,
      optionsFilterParams,
      mergeParams(STRAPI_FILTER_OPERATORS.AND, acc, filterParams),
      mergeParams(STRAPI_FILTER_OPERATORS.OR, acc, filterParams)
    );
  }, {});
};

const pickFilterConfigUtils = (
  config: FilterConfigs[number] | OptionFilterConfig
): FilterUtils => {
  const { filterVariant, filterKey } = config;

  const key =
    filterVariant === FILTER_VARIANTS.RANGE ? filterKey.from : filterKey;

  return FILTER_CONFIGS_UTILS[key];
};

export const extendFilterOptions = (
  options: OptionFilters
): ExtendedOptionFilters => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.map((option) => {
    return {
      ...option,
      filterConfigs: extendFilterConfigs(option.filterConfigs),
    };
  });
};

export const extendFilterConfigs = <T>(
  configs: T
): T extends FilterConfigs
  ? ExtendedFilterConfigs
  : ExtendedOptionFilterConfigs => {
  if (!Array.isArray(configs)) {
    return [];
  }

  return configs.map((config) => {
    const configUtils = pickFilterConfigUtils(config);

    return Object.assign(
      config,
      configUtils,
      config.filterVariant !== FILTER_VARIANTS.OPTION
        ? { options: extendFilterOptions(config.options) }
        : {}
    );
  });
};

export { checkIsValidFilterStateValue } from './checkIsValidFilterStateValue';
export { createBetweenFilterPart } from './createBetweenFilterPart';
export { createFromToFilterPart } from './createFromToFilterPart';
export { defineParamsCreator } from './defineParamsCreator';
export { isValidIndexSignature } from './isValidIndexSignature';
export { prepareParamsCreator } from './prepareParamsCreator';
